import type { PolicyDocument, User, AuditEntry, Department, Announcement, Banner, SectionType, SectionSize, AnnouncementDetail, AnnouncementComment, SessionSettings, Course, CourseDetail, CourseSection, CourseSectionType, CourseCert } from "@/types";

const API_BASE = "/api";

async function request<T>(path: string, options?: RequestInit): Promise<T | null> {
  try {
    const res = await fetch(`${API_BASE}${path}`, {
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      ...options,
    });
    if (res.status === 401) return null;
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      throw new Error(body?.error || `Request failed: ${res.status}`);
    }
    const text = await res.text();
    if (!text) return null as T;
    return JSON.parse(text);
  } catch (e) {
    throw e;
  }
}

function qs(params: Record<string, string | boolean | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined);
  if (entries.length === 0) return "";
  return "?" + entries.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join("&");
}

// Auth
export async function login(username: string, password: string): Promise<User | null> {
  const res = await request<{ user: User }>("/auth/login", {
    method: "POST",
    body: JSON.stringify({ username, password }),
  });
  return res?.user ?? null;
}

export async function register(username: string, password: string, displayName: string, payrollId: string): Promise<User | null> {
  const res = await request<{ user: User }>("/auth/register", {
    method: "POST",
    body: JSON.stringify({ username, password, displayName, payrollId }),
  });
  return res?.user ?? null;
}

export async function logout(): Promise<void> {
  await request("/auth/logout", { method: "POST" });
}

export async function getSession(): Promise<User | null> {
  const res = await request<{ user: User }>("/auth/session");
  return res?.user ?? null;
}

// Users
export async function getUsers(): Promise<User[]> {
  const res = await request<{ users: User[] }>("/users");
  return res?.users ?? [];
}

export async function changePassword(userId: string, password: string): Promise<{ ok: boolean; error?: string }> {
  return (await request(`/users/${userId}/password`, {
    method: "PUT",
    body: JSON.stringify({ password }),
  })) ?? { ok: false, error: "Request failed" };
}

export async function updateEmail(userId: string, email: string): Promise<{ ok: boolean; error?: string }> {
  return (await request(`/users/${userId}/email`, {
    method: "PUT",
    body: JSON.stringify({ email }),
  })) ?? { ok: false, error: "Request failed" };
}

export async function updateTheme(userId: string, theme: string): Promise<{ ok: boolean; theme?: string }> {
  return (await request(`/users/${userId}/theme`, {
    method: "PUT",
    body: JSON.stringify({ theme }),
  })) ?? { ok: false };
}

export async function elevateUser(userId: string): Promise<boolean> {
  const res = await request(`/users/${userId}/role`, {
    method: "PUT",
    body: JSON.stringify({ role: "admin" }),
  });
  return res !== null;
}

export async function demoteUser(userId: string): Promise<boolean> {
  const res = await request(`/users/${userId}/role`, {
    method: "PUT",
    body: JSON.stringify({ role: "user" }),
  });
  return res !== null;
}

export async function deleteUser(userId: string): Promise<boolean> {
  const res = await request(`/users/${userId}`, { method: "DELETE" });
  return res !== null;
}

// User Departments
export async function getUserDepartments(userId: string): Promise<Department[]> {
  return (await request<Department[]>(`/users/${userId}/departments`)) ?? [];
}

export async function addUserDepartment(userId: string, departmentId: string): Promise<boolean> {
  const res = await request(`/users/${userId}/departments`, {
    method: "POST",
    body: JSON.stringify({ departmentId }),
  });
  return res !== null;
}

export async function removeUserDepartment(userId: string, departmentId: string): Promise<boolean> {
  const res = await request(`/users/${userId}/departments/${departmentId}`, { method: "DELETE" });
  return res !== null;
}

// Current user's departments
export async function getMyDepartments(): Promise<Department[]> {
  return (await request<Department[]>("/user/departments")) ?? [];
}

export async function addMyDepartment(departmentId: string): Promise<boolean> {
  const res = await request("/user/departments", {
    method: "POST",
    body: JSON.stringify({ departmentId }),
  });
  return res !== null;
}

export async function removeMyDepartment(departmentId: string): Promise<boolean> {
  const res = await request(`/user/departments/${departmentId}`, { method: "DELETE" });
  return res !== null;
}

// Department Webhooks (phone numbers)
export async function getDepartmentWebhook(departmentId: string): Promise<{ phoneNumber: string | null }> {
  return (await request<{ phoneNumber: string | null }>(`/departments/${departmentId}/webhook`)) ?? { phoneNumber: null };
}

export async function setDepartmentWebhook(departmentId: string, phoneNumber: string): Promise<boolean> {
  const res = await request(`/departments/${departmentId}/webhook`, {
    method: "PUT",
    body: JSON.stringify({ phoneNumber }),
  });
  return res !== null;
}

// Global Webhook URL
export async function getGlobalWebhook(): Promise<{ webhookUrl: string | null }> {
  return (await request<{ webhookUrl: string | null }>("/settings/webhook")) ?? { webhookUrl: null };
}

export async function setGlobalWebhook(webhookUrl: string): Promise<boolean> {
  const res = await request("/settings/webhook", {
    method: "PUT",
    body: JSON.stringify({ webhookUrl }),
  });
  return res !== null;
}

// File Upload
export async function uploadFile(file: File): Promise<{ url: string } | null> {
  const formData = new FormData();
  formData.append("file", file);
  try {
    const res = await fetch(`${API_BASE}/upload`, {
      method: "POST",
      credentials: "include",
      body: formData,
    });
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      throw new Error(body?.error || `Upload failed: ${res.status}`);
    }
    return await res.json();
  } catch {
    return null;
  }
}

export type MediaKind = "video" | "pdf" | "ppt";

export interface MediaUploadResult {
  url: string | null;
  originalUrl: string | null;
  converted: boolean;
}

export async function uploadMedia(file: File, kind: MediaKind): Promise<MediaUploadResult | null> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("kind", kind);
  try {
    const res = await fetch(`${API_BASE}/media-upload`, {
      method: "POST",
      credentials: "include",
      body: formData,
    });
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      throw new Error(body?.error || `Upload failed: ${res.status}`);
    }
    return await res.json();
  } catch {
    return null;
  }
}

// Documents
export async function getDocuments(archived?: boolean): Promise<PolicyDocument[]> {
  const q = qs({ archived });
  return (await request<PolicyDocument[]>(`/documents${q}`)) ?? [];
}

export async function createDocument(title: string, dueDate?: string | null): Promise<PolicyDocument | null> {
  return request<PolicyDocument>("/documents", {
    method: "POST",
    body: JSON.stringify({ title, due_date: dueDate }),
  });
}

export async function updateDocument(id: string, data: Partial<PolicyDocument>): Promise<PolicyDocument | null> {
  return request<PolicyDocument>(`/documents/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function deleteDocument(id: string): Promise<boolean> {
  const res = await request(`/documents/${id}`, { method: "DELETE" });
  return res !== null;
}

export async function restoreDocument(id: string): Promise<boolean> {
  const res = await request(`/documents/${id}/restore`, { method: "POST" });
  return res !== null;
}

export async function getTrash(): Promise<{ id: string; title: string; deletedAt: string }[]> {
  return (await request<{ id: string; title: string; deletedAt: string }[]>("/documents/trash")) ?? [];
}

export async function duplicateDocument(id: string): Promise<PolicyDocument | null> {
  return request<PolicyDocument>(`/documents/${id}/duplicate`, { method: "POST" });
}

export async function reorderDocuments(order: { id: string; sort_order: number }[]): Promise<boolean> {
  const res = await request("/documents/reorder", {
    method: "PUT",
    body: JSON.stringify({ order }),
  });
  return res !== null;
}

// Sections
export interface SectionInput {
  title: string;
  type?: SectionType;
  content?: string;
  url?: string | null;
  originalUrl?: string | null;
  size?: SectionSize;
}

export async function createSection(docId: string, input: SectionInput): Promise<PolicyDocument | null> {
  return request<PolicyDocument>(`/documents/${docId}/sections`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function updateSection(id: string, data: Partial<SectionInput>): Promise<boolean> {
  const res = await request(`/sections/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
  return res !== null;
}

// Section media progress (video resume)
export async function getSectionProgress(sectionId: string): Promise<number> {
  const res = await request<{ seconds: number }>(`/sections/${sectionId}/progress`);
  return res?.seconds ?? 0;
}

export async function saveSectionProgress(sectionId: string, seconds: number): Promise<boolean> {
  const res = await request(`/sections/${sectionId}/progress`, {
    method: "PUT",
    body: JSON.stringify({ seconds }),
  });
  return res !== null;
}

export async function deleteSection(id: string): Promise<boolean> {
  const res = await request(`/sections/${id}`, { method: "DELETE" });
  return res !== null;
}

export async function reorderSections(order: { id: string; sort_order: number }[]): Promise<boolean> {
  const res = await request("/sections/reorder", {
    method: "PUT",
    body: JSON.stringify({ order }),
  });
  return res !== null;
}

// Tracking
export async function getTracking(): Promise<Record<string, boolean>> {
  const data = await request<Record<string, boolean>>("/tracking");
  return data ?? {};
}

export async function getAllTracking(): Promise<Record<string, string>> {
  const data = await request<Record<string, string>>("/tracking/all");
  return data ?? {};
}

export async function toggleRead(sectionId: string, read: boolean): Promise<boolean> {
  const res = await request(`/tracking/${sectionId}`, {
    method: "PUT",
    body: JSON.stringify({ read }),
  });
  return res !== null;
}

// Search
export async function search(query: string): Promise<{ documents: PolicyDocument[]; sections: { section: { id: string; title: string; content: string }; document_id: string; document_title: string }[] }> {
  const res = await request<{ documents: PolicyDocument[]; sections: any[] }>(`/search?q=${encodeURIComponent(query)}`);
  return res ?? { documents: [], sections: [] };
}

// Audit
export async function getAudit(docId: string): Promise<AuditEntry[]> {
  return (await request<AuditEntry[]>(`/documents/${docId}/audit`)) ?? [];
}

export async function getUserAudit(userId: string): Promise<AuditEntry[]> {
  return (await request<AuditEntry[]>(`/users/${userId}/audit`)) ?? [];
}

// Stats
export async function getStats(): Promise<{ totalPolicies: number; totalUsers: number; overallCompletion: number } | null> {
  return request("/stats");
}

// Trend
export interface TrendPoint {
  date: string;
  completionPct: number;
  readCount: number;
  unreadCount: number;
}

export async function getTrend(): Promise<{ trend: TrendPoint[]; totalSections: number; totalStaff: number; maxPossible: number }> {
  return (await request<{ trend: TrendPoint[]; totalSections: number; totalStaff: number; maxPossible: number }>("/trend")) ?? { trend: [], totalSections: 0, totalStaff: 0, maxPossible: 0 };
}

// Tags
export async function getTags(docId: string): Promise<string[]> {
  const res = await request<{ tags: string[] }>(`/documents/${docId}/tags`);
  return res?.tags ?? [];
}

export async function addTag(docId: string, tag: string): Promise<boolean> {
  const res = await request(`/documents/${docId}/tags`, { method: "POST", body: JSON.stringify({ tag }) });
  return res !== null;
}

export async function removeTag(docId: string, tag: string): Promise<boolean> {
  const res = await request(`/documents/${docId}/tags?tag=${encodeURIComponent(tag)}`, { method: "DELETE" });
  return res !== null;
}

// Section Versions
export interface SectionVersion {
  id: string;
  sectionId: string;
  title: string;
  content: string;
  type: SectionType;
  url: string | null;
  originalUrl: string | null;
  editedBy: string;
  editorName: string;
  createdAt: string;
}

export async function getSectionVersions(sectionId: string): Promise<SectionVersion[]> {
  return (await request<SectionVersion[]>(`/sections/${sectionId}/versions`)) ?? [];
}

export async function restoreSectionVersion(sectionId: string, versionId: string): Promise<boolean> {
  const res = await request(`/sections/${sectionId}/restore`, {
    method: "POST",
    body: JSON.stringify({ versionId }),
  });
  return res !== null;
}

// Due Dates
export interface DueDateEntry {
  id: string;
  title: string;
  dueDate: string;
  isOverdue: boolean;
  daysLeft: number;
  totalSections: number;
  readSections: number;
  completionPct: number;
}

export async function getDueDates(): Promise<DueDateEntry[]> {
  return (await request<DueDateEntry[]>("/due-dates")) ?? [];
}

// Import / Export
export async function exportData(): Promise<any> {
  return request("/export");
}

export async function importData(data: any): Promise<{ ok: boolean; imported?: any }> {
  return (await request("/import", { method: "POST", body: JSON.stringify(data) })) ?? { ok: false };
}

// Departments
export async function getDepartments(): Promise<Department[]> {
  return (await request<Department[]>("/departments")) ?? [];
}

export async function createDepartment(name: string, color?: string, icon?: string): Promise<Department | null> {
  return request<Department>("/departments", {
    method: "POST",
    body: JSON.stringify({ name, color, icon }),
  });
}

export async function updateDepartment(id: string, data: Partial<Department>): Promise<boolean> {
  const res = await request(`/departments/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
  return res !== null;
}

export async function deleteDepartment(id: string): Promise<boolean> {
  const res = await request(`/departments/${id}`, { method: "DELETE" });
  return res !== null;
}

// Announcements
export async function getAnnouncements(): Promise<Announcement[]> {
  return (await request<Announcement[]>("/announcements")) ?? [];
}

export async function createAnnouncement(data: {
  title: string;
  content?: string;
  type?: string;
  departmentIds?: string[];
  priority?: number;
  isPinned?: boolean;
  imageUrl?: string | null;
  emoji?: string | null;
  gridSize?: string;
  sendToWebhook?: boolean;
  expiresAt?: string | null;
}): Promise<string | null> {
  const res = await request<{ id: string }>("/announcements", {
    method: "POST",
    body: JSON.stringify(data),
  });
  return res?.id ?? null;
}

export async function updateAnnouncement(id: string, data: Partial<Announcement>): Promise<boolean> {
  const res = await request(`/announcements/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
  return res !== null;
}

export async function deleteAnnouncement(id: string): Promise<boolean> {
  const res = await request(`/announcements/${id}`, { method: "DELETE" });
  return res !== null;
}

export async function reorderAnnouncements(order: { id: string; sort_order: number }[]): Promise<boolean> {
  const res = await request("/announcements/reorder", {
    method: "PUT",
    body: JSON.stringify({ order }),
  });
  return res !== null;
}

// Banners
export async function getBanners(): Promise<Banner[]> {
  return (await request<Banner[]>("/banners")) ?? [];
}

export async function createBanner(data: {
  title: string;
  subtitle?: string;
  bgColor?: string;
  textColor?: string;
  gradient?: string;
  imageUrl?: string;
  linkUrl?: string;
  sendToWebhook?: boolean;
}): Promise<string | null> {
  const res = await request<{ id: string }>("/banners", {
    method: "POST",
    body: JSON.stringify(data),
  });
  return res?.id ?? null;
}

export async function updateBanner(id: string, data: Partial<Banner>): Promise<boolean> {
  const res = await request(`/banners/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
  return res !== null;
}

export async function deleteBanner(id: string): Promise<boolean> {
  const res = await request(`/banners/${id}`, { method: "DELETE" });
  return res !== null;
}

// Announcement community
export async function getAnnouncementDetail(id: string): Promise<AnnouncementDetail | null> {
  return request<AnnouncementDetail>(`/announcements/${id}`);
}

export async function toggleAnnouncementLike(id: string): Promise<{ liked: boolean; count: number } | null> {
  return request<{ liked: boolean; count: number }>(`/announcements/${id}/like`, { method: "POST" });
}

export async function addAnnouncementComment(id: string, body: string, parentId?: string | null): Promise<AnnouncementComment | null> {
  return request<AnnouncementComment>(`/announcements/${id}/comments`, {
    method: "POST",
    body: JSON.stringify({ body, parentId: parentId || null }),
  });
}

export async function deleteAnnouncementComment(commentId: string): Promise<boolean> {
  const res = await request(`/comments/${commentId}`, { method: "DELETE" });
  return res !== null;
}

export async function toggleCommentLike(commentId: string): Promise<{ liked: boolean; count: number } | null> {
  return request<{ liked: boolean; count: number }>(`/comments/${commentId}/like`, { method: "POST" });
}

// Session settings
export async function getSessionSettings(): Promise<SessionSettings | null> {
  return request<SessionSettings>("/session-settings");
}

export async function updateSessionTimeout(minutes: number | null): Promise<{ ok: boolean; ownMinutes?: number | null } | null> {
  return request(`/session-settings`, {
    method: "PUT",
    body: JSON.stringify({ minutes }),
  });
}

export async function setSessionTimeoutCap(capSeconds: number | null): Promise<{ ok: boolean; capSeconds?: number | null } | null> {
  return request("/session-settings/cap", {
    method: "PUT",
    body: JSON.stringify({ capSeconds }),
  });
}

// Training / Courses
export interface CourseInput {
  title: string;
  description?: string;
  departmentId?: string | null;
  passmarkPct?: number;
  tiers?: { min: number; title: string }[];
  expiryMonths?: number | null;
  icon?: string | null;
  difficulty?: string | null;
  orderLabel?: string | null;
}

export async function getCourses(): Promise<Course[]> {
  return (await request<Course[]>("/courses")) ?? [];
}

export async function getCourseDetail(id: string): Promise<CourseDetail | null> {
  return request<CourseDetail>(`/courses/${id}`);
}

export async function createCourse(input: CourseInput): Promise<Course | null> {
  return request<Course>("/courses", { method: "POST", body: JSON.stringify(input) });
}

export async function updateCourse(id: string, data: Partial<CourseInput>): Promise<boolean> {
  const res = await request(`/courses/${id}`, { method: "PUT", body: JSON.stringify(data) });
  return res !== null;
}

export async function deleteCourse(id: string): Promise<boolean> {
  const res = await request(`/courses/${id}`, { method: "DELETE" });
  return res !== null;
}

export async function reorderCourses(order: { id: string; sort_order: number }[]): Promise<boolean> {
  const res = await request("/courses/reorder", { method: "PUT", body: JSON.stringify({ order }) });
  return res !== null;
}

export async function createCourseSection(courseId: string, input: {
  title: string; type: CourseSectionType; content?: string; url?: string | null; originalUrl?: string | null; size?: SectionSize;
}): Promise<boolean> {
  const res = await request(`/courses/${courseId}/sections`, { method: "POST", body: JSON.stringify(input) });
  return res !== null;
}

export async function updateCourseSection(id: string, data: Partial<{ title: string; type: CourseSectionType; content?: string; url?: string | null; originalUrl?: string | null; size?: SectionSize }>): Promise<boolean> {
  const res = await request(`/course-sections/${id}`, { method: "PUT", body: JSON.stringify(data) });
  return res !== null;
}

export async function deleteCourseSection(id: string): Promise<boolean> {
  const res = await request(`/course-sections/${id}`, { method: "DELETE" });
  return res !== null;
}

export async function reorderCourseSections(order: { id: string; sort_order: number }[]): Promise<boolean> {
  const res = await request("/course-sections/reorder", { method: "PUT", body: JSON.stringify({ order }) });
  return res !== null;
}

export interface AttemptStartResult {
  attemptId: string;
  status: string;
  resumed: boolean;
}

export interface SubmitAnswer {
  sectionId: string;
  questionIndex: number;
  answer: string;
}

export async function startCourseAttempt(courseId: string): Promise<AttemptStartResult | null> {
  return request<AttemptStartResult>(`/courses/${courseId}/attempts`, { method: "POST" });
}

export async function submitCourseAttempt(attemptId: string, answers: SubmitAnswer[]): Promise<any | null> {
  return request(`/attempts/${attemptId}/submit`, {
    method: "PUT",
    body: JSON.stringify({ answers }),
  });
}

export interface GradeableAnswer {
  id: string;
  sectionTitle: string;
  questionIndex: number;
  question: string;
  kind: string;
  answer: string;
  points: number | null;
  maxPoints: number;
  gradedBy: string | null;
}

export interface GradingAttempt {
  attemptId: string;
  userId: string;
  userName: string;
  status: string;
  submittedAt: string | null;
  gradedAt: string | null;
  finalPct: number | null;
  openPending: number;
  earned: number;
  max: number;
  answers: GradeableAnswer[];
}

export async function getCourseGrading(courseId: string): Promise<GradingAttempt[]> {
  return (await request<GradingAttempt[]>(`/courses/${courseId}/grading`)) ?? [];
}

export async function gradeOpenAnswer(answerId: string, points: number): Promise<any | null> {
  return request(`/answers/${answerId}/grade`, { method: "PUT", body: JSON.stringify({ points }) });
}

export async function getMyCertificates(): Promise<CourseCert[]> {
  return (await request<CourseCert[]>("/me/certificates")) ?? [];
}

export async function rateCourse(courseId: string, stars: number, comment?: string): Promise<{ ok: boolean; stars?: number } | null> {
  return request(`/courses/${courseId}/rating`, { method: "POST", body: JSON.stringify({ stars, comment: comment || "" }) });
}
